/* eslint-disable max-len */
import dayjs from "dayjs";
import React, { useMemo, useEffect, useState } from "react";
import {
  Box,
  Card,
  Grid,
  Button,
  Dialog,
  Divider,
  Checkbox,
  Typography,
  DialogContent,
  FormControlLabel,
  DialogActions,
  DialogTitle,
  IconButton,
  CardContent,
  CardHeader,
} from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Edit, Close, WarningRounded, Home } from "@material-ui/icons";

import InputActive from "../../InputActive";
import PreviewModal from "../../../../components/CustomComponents/PreviewModal";

// VI.0 (QCV 2026): nhúng trực tiếp các trình kê khai thành tích KHCN vào form
// đăng ký (giống Giải thưởng Nữ sinh) — các component này là CRUD độc lập.
import BangSangChe from "../../../../components/QCV/BangSangChe";
import BaiBao from "../../../../components/QCV/BaiBao";
import BaoCao from "../../../../components/QCV/BaoCao";
import DuAn from "../../../../components/QCV/DuAnNghienCuu";
import SachChuyenKhao from "../../../../components/QCV/SachChuyenKhao";
import DoanhNghiep from "../../../../components/QCV/DoanhNghiep";
import GiaiThuong from "../../../../components/QCV/GiaiThuong";

import theme from "../../../../theme";
import KPTextform from "../../../../components/KPTComponents/KPTextform";
import exportProfile from "../../../../utils/qcv.exportProfile";
// import fileCongVanMau from "../../../../assets/docs/QCV2025_Mẫu công văn giới thiệu.docx";

import { photoRender } from "../../../../utils/photoRender";
import { useScreenSize } from "../../../../hooks/useScreenSize";
import {
  useEditingProfile,
  useIsAdminEditing,
} from "../../../../hooks/useEditingProfile";
import { jumpToReleventDiv } from "../../../../components/KPTComponents/utils";
import { ROUTER, FORM_CONSTANTS } from "../../../../constants";
import {
  QCV2026_LINKS,
  QCV2026_CONTACTS,
  QCV2026_REWARD_LEVEL_OPTIONS,
  QCV2026_TYPICAL_WORK_FIELDS,
  composeTypicalWorkHtml,
} from "../../../../constants/qcv2026.config";
import { capitalizeFirstLetter } from "../../../../utils";
import {
  adminEditingActions,
  fileActions,
  formActions,
  userActions,
} from "../../../../actions";
import { canEditCandidateProfile } from "../../../../utils/permission";
import {
  ACHIEVEMENT_TYPE,
  CATEGORY,
  KEYS,
  SECONDARY_COLOR,
} from "../../../../constants/enums";

// Tên danh sách tương ứng trong payload của reloadSeasonAchievements (số nhiều)
// cho từng loại thành tích.
const ACHIEVEMENT_PAYLOAD_KEY = {
  [ACHIEVEMENT_TYPE.REWARD]: "rewards",
  [ACHIEVEMENT_TYPE.SOLUTION]: "solutions",
  [ACHIEVEMENT_TYPE.MONOGRAPH]: "monographs",
  [ACHIEVEMENT_TYPE.TECH_COMPANY]: "techCompanies",
  [ACHIEVEMENT_TYPE.HR_DEVELOPMENT]: "hrDevelopments",
  [ACHIEVEMENT_TYPE.RESEARCH_PROJECT]: "researchProjects",
  [ACHIEVEMENT_TYPE.SCIENTIFIC_REPORT]: "scientificReports",
  [ACHIEVEMENT_TYPE.SCIENTIFIC_RESEARCH]: "scientificResearches",
};

// Chụp lại kết quả chấm của bảng nối ngay khi nạp hồ sơ, khoá theo
// "<loại>:<id thành tích>".
//
// Vì sao phải chụp riêng thay vì đọc thẳng từ Redux lúc lưu: khi ứng viên sửa
// nội dung một thành tích, reducer UPDATE_<X>_SUCCESS THAY nguyên khối bản ghi
// bằng payload thô của API thành tích - payload đó không có form<X>Id/score/
// maxScore/accepted. Đọc Redux lúc đó sẽ tưởng thành tích này chưa được chấm và
// ghi đè điểm về 0 khi lưu hồ sơ.
const buildScoreMap = (payload) => {
  const map = {};
  Object.entries(ACHIEVEMENT_PAYLOAD_KEY).forEach(([type, listKey]) => {
    (payload?.[listKey] || []).forEach((e) => {
      if (e?.id === undefined || e?.id === null) return;
      map[`${type}:${e.id}`] = {
        score: e.score ?? 0,
        maxScore: e.maxScore ?? 0,
        accepted: e.accepted ?? false,
        note: e.note ?? "",
      };
    });
  });
  return map;
};

function DangKyHoSoUpdate() {
  const dispatch = useDispatch();
  const isMobileMode = useScreenSize();

  const [files, setFiles] = useState(null);
  const [values, setValues] = useState(null);
  const [preview, setPreview] = useState(null);
  const [showCSKH, setShowCSKH] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  // QCV 2026: thành tích khai inline ngay trong form nên KHÔNG bật modal nhắc
  // "hoàn thành thành tích trước". Modal chỉ dùng cho: cổng chưa mở/hết hạn
  // (isNotAvalaible) hoặc thông báo hồ sơ đã nộp (set true qua useEffect bên dưới).
  const [warningOpen, setWarningOpen] = useState(false);
  const [acceptRules, setAcceptRules] = useState(false);
  const [socialActivity, setSocialActivity] = useState([]);
  const [personalProcess, setPersonalProcess] = useState([]);
  const [otherAchievement, setOtherAchievement] = useState([]);
  // QCV 2026: C.1 bồi dưỡng nhân lực, D ưu tiên, Cam kết ứng viên
  const [hrTraining, setHrTraining] = useState([]);
  const [priority, setPriority] = useState({});
  const [commitment, setCommitment] = useState({});
  // Kết quả chấm của bảng nối, chụp lúc nạp hồ sơ - xem buildScoreMap.
  const [scoreByAchievement, setScoreByAchievement] = useState({});

  const application = useSelector(
    (state) => state.formReducer.applicationList?.[0] || null
  );

  const loading = useSelector((state) => {
    const loadingUser = state.userReducer.loading;
    const loadingForm = state.formReducer.loading;
    const loadingProfile = state.profileReducer.loading;
    return loadingUser || loadingForm || loadingProfile || isLoading;
  });

  const { seasons, openSeasonId } = useSelector((state) => ({
    seasons: state.seasonReducer.seasonList,
    openSeasonId: state.seasonReducer.openSeasonId,
  }));

  // Hồ sơ đang sửa: của chính ứng viên, hoặc của người mà quản trị viên đang
  // sửa hộ khi vào qua /dang-ky-ho-so/:profileId.
  const profile = useEditingProfile();
  const isAdminEditing = useIsAdminEditing();

  const { profileId: routeProfileId } = useParams();
  const role = useSelector((state) => state.userReducer.user?.role);
  const canEditOthers = canEditCandidateProfile(role);

  // Bảng điểm (mục 1-7): dùng để tra "Phân loại" khi xuất file .docx theo code
  // mới (qcv_score_infos), thay cho tra cứu hardcode.
  const scoreInformationList = useSelector(
    (state) => state.scoreInformationReducer.scoreInformationList
  );

  const achievementData = useSelector((state) => ({
    [ACHIEVEMENT_TYPE.REWARD]: state.rewardReducer.personalRewardList,
    [ACHIEVEMENT_TYPE.SOLUTION]: state.solutionReducer.personalSolutionList,
    [ACHIEVEMENT_TYPE.MONOGRAPH]: state.monographReducer.personalMonographList,
    [ACHIEVEMENT_TYPE.TECH_COMPANY]:
      state.techCompanyReducer.personalTechCompanyList,
    [ACHIEVEMENT_TYPE.HR_DEVELOPMENT]:
      state.hrDevelopmentReducer.personalHrDevelopmentList,
    [ACHIEVEMENT_TYPE.RESEARCH_PROJECT]:
      state.researchProjectReducer.personalResearchProjectList,
    [ACHIEVEMENT_TYPE.SCIENTIFIC_REPORT]:
      state.reportsReducer.personalReportList,
    [ACHIEVEMENT_TYPE.SCIENTIFIC_RESEARCH]:
      state.researchReducer.personalResearchList,
    [ACHIEVEMENT_TYPE.HR_DEVELOPMENT + KEYS.DAO_TAO]:
      state.hrDevelopmentReducer.personalHrDevelopmentList.filter(
        (e) => e.type === KEYS.DAO_TAO
      ),
    [ACHIEVEMENT_TYPE.HR_DEVELOPMENT + KEYS.BOI_DUONG]:
      state.hrDevelopmentReducer.personalHrDevelopmentList.filter(
        (e) => e.type === KEYS.BOI_DUONG
      ),
    [ACHIEVEMENT_TYPE.SCIENTIFIC_RESEARCH + KEYS.DOMESTIC]:
      state.researchReducer.personalResearchList.filter(
        (e) => e.filterType === KEYS.DOMESTIC
      ),
    [ACHIEVEMENT_TYPE.SCIENTIFIC_RESEARCH + KEYS.INTERNATIONAL]:
      state.researchReducer.personalResearchList.filter(
        (e) => e.filterType === KEYS.INTERNATIONAL
      ),
  }));

  const openSeason = useMemo(
    () => seasons.find((s) => s.id === openSeasonId),
    [seasons, openSeasonId]
  );

  const isNotAvalaible = useMemo(() => {
    if (!openSeason) return false;
    // Quản trị viên sửa hộ được phép sửa cả khi cổng chưa mở / đã đóng.
    if (isAdminEditing) return null;
    const { openDate, closeDate } = openSeason;
    const currDate = new Date();
    if (currDate < new Date(openDate)) return "Chưa mở cổng đăng ký";
    if (currDate > new Date(closeDate)) return "Đã hết thời hạn đăng ký";
    return null;
  }, [openSeason, isAdminEditing]);

  const handleFileChange = (e, name) => setFiles({ ...files, [name]: e });

  const safeParseJSON = (str, fallback) => {
    try {
      return JSON.parse(str) || fallback;
    } catch (e) {
      return fallback;
    }
  };

  const handleChangeActivityForm = (name, event, type, values, setValues) => {
    const keyList = name.split(".");
    const key = keyList[2];
    const index = parseInt(keyList[1]);

    const tmp = [...values];
    if (type === "date") {
      tmp.splice(index, 1, { ...tmp[index], [key]: event });
    } else if (type === "removeFile") {
      tmp.splice(index, 1, { ...tmp[index], [key]: undefined });
    } else {
      tmp.splice(index, 1, { ...tmp[index], [key]: event.target.value });
    }
    setValues(tmp);
  };

  const handleReload = (profileId) => {
    // Nạp danh sách thành tích (bài báo, báo cáo, sách, đề tài, sáng chế,
    // giải thưởng, sản phẩm KHCN) CHỈ của season đang mở - KHÔNG dùng
    // getApplicationFormv2 (lấy tất cả các season ứng viên từng đăng ký,
    // gây lẫn dữ liệu giữa các mùa giải khi lưu hồ sơ).
    dispatch(
      formActions.reloadSeasonAchievements(profileId, openSeasonId, CATEGORY.QCV)
    ).then((payload) => {
      if (payload) setScoreByAchievement(buildScoreMap(payload));
    });
    dispatch(
      formActions.filterApplicationForm({
        // Dùng profileId ĐƯỢC TRUYỀN VÀO, không phải profile.id - ở chế độ quản
        // trị viên sửa hộ thì hai giá trị này khác nhau.
        category: CATEGORY.QCV,
        seasonId: openSeasonId,
        profileId,
      })
    ).then((applications) => {
      if (applications.length > 0) {
        const application = applications[0];
        if (application.process === "") application.process = "[]";
        if (application.previewFile === "") application.previewFile = "[]";
        if (application.socialActivity === "")
          application.socialActivity = "[]";
        if (application.communicationActivity === "")
          application.communicationActivity = "[]";

        const fileList = safeParseJSON(application?.previewFile, []);
        application.sumaryFile = (
          Array.isArray(fileList) ? fileList : []
        ).filter((file) => typeof file === "string" && file.includes("sumaryFile"));
        // QCV 2026: chuyển url đã lưu thành mảng để DropzoneArea hiển thị lại
        application.confirmationFile = application.confirmationFile
          ? [application.confirmationFile]
          : undefined;
        // Công trình tiêu biểu: url minh chứng đã lưu -> mảng để DropzoneArea hiển thị
        application.typicalWorkFile = application.typicalWorkFile
          ? [application.typicalWorkFile]
          : undefined;
        application.activityImages = safeParseJSON(
          application.activityImages,
          []
        );
        // Công trình tiêu biểu: nạp nội dung 4 mục từ typicalWorkContent;
        // hồ sơ cũ (chỉ có summary) thì đưa vào mục 1.
        const twContent = safeParseJSON(application.typicalWorkContent, null);
        if (twContent) {
          application.work1 = twContent.work1 || "";
          application.work2 = twContent.work2 || "";
          application.work3 = twContent.work3 || "";
          application.work4 = twContent.work4 || "";
        } else if (application.summary && application.summary.trim()) {
          // Hồ sơ cũ: bỏ thẻ HTML, đưa nội dung cũ vào mục 1
          application.work1 = application.summary.replace(/<[^>]*>/g, " ").trim();
        }

        if (application) {
          setValues({ ...values, ...application });
          setSocialActivity(safeParseJSON(application.socialActivity, []));
          setPersonalProcess(safeParseJSON(application.process, []));
          setOtherAchievement(
            safeParseJSON(application.communicationActivity, [])
          );
          // QCV 2026
          setHrTraining(safeParseJSON(application.hrTrainingActivity, []));
          setPriority(safeParseJSON(application.priority, {}));
          setCommitment(safeParseJSON(application.commitment, {}));
        }
      } else {
        setValues({ ...values, summary: " " });
      }
    });
  };

  const handleSave = async (requestedStatus) => {
    // Quản trị viên sửa hộ thì KHÔNG đổi trạng thái hồ sơ: hồ sơ đã duyệt vẫn
    // là đã duyệt. Đổi trạng thái vẫn làm ở panel chấm điểm bên /ho-so/:id.
    // Cũng nhờ vậy không vô tình kích hoạt email "đã nhận hồ sơ" mà backend gửi
    // khi trạng thái chuyển sang in_review (application_form_service.Update).
    const status =
      isAdminEditing && application?.status ? application.status : requestedStatus;

    const formFields = [
      ...textForm.partI,
      ...textForm.partII,
      ...textForm.summary,
    ];
    // console.log("formFields", formFields, status);
    // console.log(application);
    for (const elm of formFields) {
      if (
        (status === "unsubmit" || isAdminEditing) &&
        elm.key === "sumaryFile"
      )
        continue;
      // if (status === "unsubmit") continue;

      const elmValue = elm?.value || values?.[elm?.key];
      if (!elm?.disabled && elm?.required && !elmValue) {
        setValues({ ...values, emptyKey: elm.key });
        jumpToReleventDiv(elm.key);
        dispatch(
          userActions.notification({
            message: `Không được để trống thông tin về: ${elm?.label}`,
            type: "error",
          })
        );
        return false;
      }
    }

    if (!profile?.id) {
      dispatch(
        userActions.notification({
          message: "Lỗi không xác định, vui lòng tải lại trang",
          type: "error",
        })
      );
      return false;
    }

    // Gộp các file mới (xác nhận + 05 ảnh hoạt động) vào danh sách upload.
    // importMultiFileQCV chỉ upload các phần tử là File; url cũ (string) được giữ nguyên.
    const uploadFiles = { ...files };
    const confInput = values?.confirmationFile?.[0];
    if (confInput instanceof File) uploadFiles.confirmationFile = confInput;
    const twFileInput = values?.typicalWorkFile?.[0];
    if (twFileInput instanceof File) uploadFiles.typicalWorkFile = twFileInput;
    (values?.activityImages || []).forEach((f, i) => {
      if (f instanceof File) uploadFiles["activityImage" + i] = f;
    });

    // Chờ upload file xong rồi mới dựng payload và lưu — để nút "tải file xin
    // xác nhận" có thể await đầy đủ trước khi xuất file.
    const response = await dispatch(
      fileActions.importMultiFileQCV(profile?.id, uploadFiles)
    );
    // Bảng nối (formXxx) bị backend XOÁ SẠCH rồi tạo lại đúng theo payload này
    // (application_form_repo.go -> formRepository.Update). Trước đây chỗ này chỉ
    // gửi mỗi <key>Id, nên MỖI LẦN bấm Lưu là toàn bộ điểm chấm / khung điểm /
    // ghi chú / trạng thái xác nhận của hội đồng bị tạo lại bằng giá trị mặc
    // định (0 / rỗng / false) -> mất sạch kết quả đã chấm.
    // Vì vậy phải trả lại nguyên vẹn các trường của bảng nối.
    //
    // KHÔNG gửi `id` của bản ghi nối: backend xoá cứng rồi Create lại, chèn kèm
    // khoá chính cũ sẽ không đẩy sequence của bảng -> dễ trùng khoá về sau.
    const achivementFormData = {};
        [
          ACHIEVEMENT_TYPE.REWARD,
          ACHIEVEMENT_TYPE.SOLUTION,
          ACHIEVEMENT_TYPE.MONOGRAPH,
          ACHIEVEMENT_TYPE.TECH_COMPANY,
          ACHIEVEMENT_TYPE.HR_DEVELOPMENT,
          ACHIEVEMENT_TYPE.RESEARCH_PROJECT,
          ACHIEVEMENT_TYPE.SCIENTIFIC_REPORT,
          ACHIEVEMENT_TYPE.SCIENTIFIC_RESEARCH,
        ].forEach((key) => {
          const formKey = "form" + capitalizeFirstLetter(key);
          achivementFormData[formKey] = achievementData[key].map((e) => {
            const row = { [key + "Id"]: e.id };
            // Ưu tiên bản chụp lúc nạp hồ sơ; nếu không có thì lấy từ chính bản
            // ghi trong Redux (còn nguyên khi ứng viên chưa sửa thành tích đó).
            // Thành tích vừa thêm mới không khớp cả hai -> để backend dùng giá
            // trị mặc định, tránh lấy nhầm cột `note` của bảng thành tích làm
            // ghi chú của hội đồng.
            const kept =
              scoreByAchievement[`${key}:${e.id}`] ||
              (e?.[`${formKey}Id`]
                ? {
                    score: e.score ?? 0,
                    maxScore: e.maxScore ?? 0,
                    accepted: e.accepted ?? false,
                    note: e.note ?? "",
                  }
                : null);
            if (kept) Object.assign(row, kept);
            return row;
          });
        });

        const getFile = (key, index) =>
          response?.find((file) => {
            const name = file.split(
              `/storage/awardRegister/profile/${profile?.id}/`
            )[1];
            const keyList = name.split(".");
            return keyList[0] === key && parseInt(keyList[1]) === index;
          });

        const _process = personalProcess.filter(
          (elm) => Object.values(elm).length !== 0
        );
        const _socialActivity = socialActivity
          .filter((elm) => Object.values(elm).length !== 0)
          .map((elm, index) => ({
            ...elm,
            communityAchievementFile:
              getFile("socialActivity", index) ||
              elm?.communityAchievementFile ||
              [],
          }));
        const _communicationActivity = otherAchievement
          .filter((elm) => Object.values(elm).length !== 0)
          .map((elm, index) => ({
            ...elm,
            achievementFile:
              getFile("otherAchievement", index) || elm?.achievementFile || [],
          }));
        const _hrTraining = hrTraining
          .filter((elm) => Object.values(elm).length !== 0)
          .map((elm, index) => ({
            ...elm,
            hrTrainingFile:
              getFile("hrTraining", index) || elm?.hrTrainingFile || [],
          }));

        var previewFile = safeParseJSON(application?.previewFile, []);
        if (!Array.isArray(previewFile)) previewFile = [];
        const sumaryFiles = response?.filter((file) =>
          file.includes("sumaryFile")
        );
        if (sumaryFiles.length > 0) previewFile = sumaryFiles;

        // QCV 2026: file xác nhận (giữ url cũ nếu không upload mới)
        const confirmationFile =
          response?.find((file) => file.includes("confirmationFile")) ||
          (typeof values?.confirmationFile?.[0] === "string"
            ? values.confirmationFile[0]
            : application?.confirmationFile) ||
          "";
        // Minh chứng công trình tiêu biểu (giữ url cũ nếu không upload mới)
        const typicalWorkFile =
          response?.find((file) => file.includes("typicalWorkFile")) ||
          (typeof values?.typicalWorkFile?.[0] === "string"
            ? values.typicalWorkFile[0]
            : application?.typicalWorkFile) ||
          "";
        // 05 ảnh hoạt động: url cũ (string) + url mới upload
        const existingImages = (values?.activityImages || []).filter(
          (f) => typeof f === "string"
        );
        const newImages =
          response?.filter((file) => file.includes("activityImage")) || [];
        const activityImages = [...existingImages, ...newImages];

        const typicalWorkData = {
          work1: values?.work1 || "",
          work2: values?.work2 || "",
          work3: values?.work3 || "",
          work4: values?.work4 || "",
        };

        const payload = {
          ...values,
          ...achivementFormData,
          status,
          seasonId: openSeasonId,
          category: CATEGORY.QCV,
          profileId: profile.id,
          process: JSON.stringify(_process),
          socialActivity: JSON.stringify(_socialActivity),
          communicationActivity: JSON.stringify(_communicationActivity),
          previewFile: JSON.stringify(previewFile),
          // QCV 2026
          hrTrainingActivity: JSON.stringify(_hrTraining),
          priority: JSON.stringify(priority),
          commitment: JSON.stringify(commitment),
          confirmationFile,
          typicalWorkFile,
          activityImages: JSON.stringify(activityImages),
          // Công trình tiêu biểu: lưu nội dung 4 mục (typicalWorkContent) và
          // tổng hợp HTML vào `summary` để hiển thị/xuất file vẫn hoạt động.
          typicalWorkContent: JSON.stringify(typicalWorkData),
          summary: composeTypicalWorkHtml(typicalWorkData),
        };

    let success;
    if (!application) {
      // Hồ sơ rỗng thì tạo hồ sơ mới
      success = await dispatch(formActions.insertApplicationForm(payload));
    } else {
      // không thì cập nhật đè lên hồ sơ cũ
      success = await dispatch(formActions.updateApplicationForm(payload));
    }
    if (success && status === "in_review" && !isAdminEditing)
      window.location.reload();
    return success;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSave("in_review");
  };

  // Vào bằng /dang-ky-ho-so/:profileId -> nạp profile ứng viên cần sửa hộ.
  // CHỈ super_admin mới được; người khác (kể cả qcv_admin) bị đưa về form của
  // chính mình (hook useEditingProfile cũng chặn lần hai theo role).
  useEffect(() => {
    const targetId = parseInt(routeProfileId);
    if (!isNaN(targetId) && canEditOthers) {
      dispatch(adminEditingActions.loadEditingProfile(targetId));
    }
    // Rời trang thì xoá ngữ cảnh sửa hộ, tránh còn sót sang màn khác.
    return () => {
      dispatch(adminEditingActions.clearEditingProfile());
    };
  }, [routeProfileId, canEditOthers]);

  useEffect(() => {
    // Vừa vào trang thì lấy thông tin hồ sơ luôn
    if (
      !isNaN(profile?.id) &&
      openSeasonId &&
      !isNaN(openSeasonId) &&
      !application
    ) {
      handleReload(profile.id);
    } else if (isNaN(profile?.id)) {
      setValues({ ...values, summary: " " });
    }
  }, [openSeasonId, profile?.id]);

  useEffect(() => {
    if (values?.sumaryFile?.[0]) {
      setFiles({ ...files, sumaryFile: values?.sumaryFile?.[0] });
    }
  }, [values?.sumaryFile]);

  useEffect(() => {
    // Hồ sơ đã nộp -> thông báo cho ứng viên (có thể đóng để xem/chỉnh sửa).
    // Quản trị viên sửa hộ thì không cần nhắc.
    if (application?.status === "in_review" && !isAdminEditing) setWarningOpen(true);
  }, [application?.status, isAdminEditing]);

  const textForm = {
    base: [
      {
        key: "name",
        width: isMobileMode ? 12 : 8,
        label: "Họ và tên",
        value: profile?.name,
        required: true,
        disabled: true,
      },
      {
        key: "birth",
        width: isMobileMode ? 12 : 4,
        label: "Ngày sinh",
        value: dayjs(profile?.birth).format("DD/MM/YYYY"),
        type: "date",
        disabled: true,
        required: true,
        note: "Giải thưởng chỉ áp dụng cho nhà khoa học trẻ không quá 35 tuổi.",
      },
      {
        key: "gender",
        width: isMobileMode ? 6 : 3,
        label: "Giới tính",
        value: profile?.gender,
        options: FORM_CONSTANTS.GENDER_OPTIONS,
        disabled: true,
      },
      {
        key: "ethnic",
        width: isMobileMode ? 6 : 3,
        label: "Dân tộc",
        value: profile?.ethnic,
        disabled: true,
      },
      {
        key: "phone",
        width: isMobileMode ? 12 : 6,
        label: "Số điện thoại",
        value: profile?.phone,
        required: true,
        disabled: true,
      },
      {
        key: "email",
        width: 12,
        label: "Email",
        value: profile?.email,
        required: true,
        disabled: true,
      },
      {
        key: "website",
        width: 12,
        height: 1,
        label: "Website/Facebook",
        required: true,
        value: profile?.website,
        disabled: true,
      },
    ],
    partI: [
      {
        key: "unit",
        width: 12,
        label: "Đơn vị học tập, công tác",
        required: true,
      },
      {
        key: "field",
        width: 12,
        label: "Lĩnh vực",
        required: true,
        // Đánh số 1. 2. 3.… trước tên lĩnh vực (theo đúng thứ tự lưu ý chọn
        // lĩnh vực). Chỉ thêm số khi hiển thị trong dropdown, không đổi `value`.
        options: FORM_CONSTANTS.FIELD_OPTIONS[CATEGORY.QCV].map((opt, i) => ({
          ...opt,
          title: `${i + 1}. ${opt.title}`,
        })),
        labelRender: (
          <Typography className="kpt-textfield-label">
            Lĩnh vực
            <span style={{ color: theme.palette.secondary.main }}>*</span>:
            <i style={{ display: "block", fontSize: 13, fontWeight: 400, color: theme.palette.secondary.main }}>
              Lưu ý: Mỗi hồ sơ chỉ đăng ký một lĩnh vực xét giải thưởng. Hồ sơ đăng ký không đúng lĩnh vực sẽ không được đưa vào xét giải. (Di chuột vào từng lĩnh vực để xem hướng dẫn chọn lĩnh vực)
            </i>
          </Typography>
        ),
      },
      { key: "nominator", width: 12, label: "Đơn vị đề cử", required: true },
      {
        key: "facultyPhone",
        width: 12,
        label: "Link file công văn cơ quan, đơn vị đề cử",
        labelRender: (
          <Typography
            className="kpt-textfield-label"
            style={{ display: "inline" }}
          >
            {`Link file công văn cơ quan, đơn vị đề cử (Tải file mẫu `}
            <a
              href={QCV2026_LINKS.mauCongVanDeCu}
              target="_blank"
              rel="noreferrer"
              style={{ color: SECONDARY_COLOR }}
            >
              tại đây.
            </a>
            {")"}
            <i style={{ display: "block", fontSize: 13, fontWeight: 400, color: theme.palette.secondary.main }}>
              Lưu ý: Vui lòng kiểm tra và thiết lập quyền truy cập đối với link Google Drive trước khi gửi để đảm bảo Ban tổ chức có thể truy cập.
            </i>
          </Typography>
        ),
        required: true,
      },
    ],
    partII: [
      {
        key: "academicLevel",
        width: 6,
        label: "Trình độ học vấn",
        required: true,
        value: values?.academicLevel,
        options: FORM_CONSTANTS.ACADEMIC_LEVEL_OPTIONS,
      },
      {
        key: "academicRank",
        width: 6,
        label: isMobileMode
          ? "Học hàm/chức danh"
          : "Học hàm/chức danh khoa học",
        value: values?.academicRank,
      },
      {
        key: "languague",
        width: 6,
        label: "Ngoại ngữ",
        required: true,
        value: values?.languague,
      },
      {
        key: "politicalUnion",
        width: 6,
        label: "Đoàn thể chính trị",
        required: true,
        value: values?.politicalUnion,
        options: FORM_CONSTANTS.POLITICAL_UNION_OPTIONS,
      },
      {
        key: "gifted",
        width: 12,
        height: 2,
        label: "Năng khiếu",
        required: true,
        value: values?.gifted,
      },
      {
        key: "videoUrl",
        width: 12,
        label: "Link video giới thiệu bản thân và 05 ảnh về hoạt động cá nhân trong học tập, nghiên cứu và công tác",
        required: true,
        value: values?.videoUrl,
        labelRender: (
          <Typography className="kpt-textfield-label">
            Link video giới thiệu bản thân và 05 ảnh về hoạt động cá nhân trong học tập, nghiên cứu và công tác
            <span style={{ color: theme.palette.secondary.main }}>*</span>:
            <i style={{ display: "block", fontSize: 13, fontWeight: 400, color: theme.palette.secondary.main }}>
              Lưu ý:
              <span style={{ display: "block" }}>
                + Video giới thiệu cá nhân không quá 02 phút, giới thiệu về cá
                nhân, các thành tích, công trình nghiên cứu khoa học hoặc giải
                pháp công nghệ tiêu biểu, giá trị khoa học, khả năng ứng dụng và
                tác động xã hội.
              </span>
              <span style={{ display: "block" }}>
                + Video, hình ảnh phải bảo đảm chất lượng, video âm thanh rõ,
                hình thức đẹp, chuyên nghiệp, dung lượng và định dạng phù hợp để
                Ban Tổ chức sử dụng, thiết kế các sản phẩm truyền thông trên các
                nền tảng số.
              </span>
              {/* <span style={{ display: "block" }}>
                + 05 ảnh về hoạt động cá nhân trong học tập, nghiên cứu và công
                tác.
              </span> */}
              <span style={{ display: "block" }}>
                + Đề nghị đặt tên ảnh theo cấu trúc: Tên ứng viên – Đơn vị công
                tác – Mô tả ngắn gọn nội dung ảnh.
              </span>
              <span style={{ display: "block" }}>
                + Vui lòng kiểm tra và thiết lập quyền truy cập đối với link Google Drive trước khi gửi để đảm bảo Ban tổ chức có thể truy cập.
              </span>
            </i>
          </Typography>
        ),
      },
    ],
    summary: [
      {
        key: "typical-work-note",
        width: 12,
        value: true,
        render: (
          <Typography
            variant="body2"
            style={{ fontStyle: "italic", color: SECONDARY_COLOR }}
          >
            Lưu ý: Bản giới thiệu công trình tiêu biểu không quá 1000 từ. Minh
            chứng cụ thể được xác nhận của cơ quan, đơn vị chuyên môn hay có thẩm
            quyền (Bản scan dấu đỏ). Ứng viên tự giới thiệu, đánh giá 01 công
            trình tiêu biểu nhất đã công bố và làm nổi bật 04 nội dung dưới đây:
          </Typography>
        ),
      },
      {
        key: "typicalWorkName",
        width: 12,
        height: 1,
        label: "Tên công trình tiêu biểu",
        value: values?.typicalWorkName,
        required: true,
      },
      ...QCV2026_TYPICAL_WORK_FIELDS.map((f, i) => ({
        key: f.key,
        width: 12,
        height: 4,
        value: values?.[f.key],
        required: true,
        label: `Công trình tiêu biểu - ${f.label}`,
        labelRender: (
          <Typography className="kpt-textfield-label">
            {`${i + 1}) ${f.label}`}
            <span style={{ color: theme.palette.secondary.main }}>*</span>:
            <i style={{ display: "block", fontSize: 13, fontWeight: 400 }}>
              {f.hint}
            </i>
          </Typography>
        ),
      })),
      {
        key: "typicalWorkFile",
        width: 12,
        type: "file",
        filesLimit: 1,
        label: "File minh chứng công trình tiêu biểu",
        value: values?.typicalWorkFile,
        required: true,
        labelRender: (
          <Typography className="kpt-textfield-label">
            Upload file minh chứng tại đây
            <span style={{ color: theme.palette.secondary.main }}>*</span>:
            <i style={{ display: "block", fontSize: 13, fontWeight: 400 }}>
              Minh chứng cụ thể được xác nhận của cơ quan, đơn vị chuyên môn hay
              có thẩm quyền (Bản scan dấu đỏ).
            </i>
          </Typography>
        ),
      },
    ],
    actionPlan: [
      {
        key: "sumaryFile",
        width: 12,
        label: "Link file Cam kết của ứng viên",
        labelRender: (
          <>
            <Typography variant="body1" style={{ fontSize: 17 }}>
              <b>Link file Cam kết của ứng viên</b>
            </Typography>
            {QCV2026_LINKS.mauCamKet ? (
              <Typography variant="body1">
                (Tải mẫu&nbsp;
                <a
                  href={QCV2026_LINKS.mauCamKet}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(event) => event.stopPropagation()}
                  style={{ textDecoration: "underline", color: SECONDARY_COLOR }}
                >
                  tại đây
                </a>
                )
              </Typography>
            ) : null}
          </>
        ),
        type: "file",
        required: true,
      },
    ],
    confirmation: [
      {
        key: "confirmationFile",
        width: 12,
        type: "file",
        filesLimit: 1,
        value: values?.confirmationFile,
        required: true,
        labelRender: (
          <>
            <Typography variant="body1" style={{ fontSize: 17 }}>
              <b>Tải file xin xác nhận</b>
            </Typography>
            <Typography variant="body1">
              Trước khi ấn nộp hồ sơ, tải file Kê khai thành tích cá nhân (trích
              xuất&nbsp;
              <a
                onClick={async (event) => {
                  event.stopPropagation();
                  setIsLoading(true);
                  try {
                    // Lưu xong mới xuất file. handleSave giờ await đầy đủ
                    // (upload + insert/update) và trả về kết quả lưu.
                    const saved = await handleSave("unsubmit");
                    if (saved) {
                      // Lấy lại hồ sơ vừa lưu để file xuất ra đúng & đủ dữ liệu
                      // mới nhất (thay vì dùng `application` cũ trong closure).
                      const apps = await dispatch(
                        formActions.filterApplicationForm({
                          category: CATEGORY.QCV,
                          seasonId: openSeasonId,
                          profileId: profile.id,
                        })
                      );
                      exportProfile(apps?.[0] || application, scoreInformationList);
                    }
                  } finally {
                    setIsLoading(false);
                  }
                }}
                style={{ textDecoration: "underline", color: SECONDARY_COLOR }}
              >
                tại đây
              </a>
              ) và xin xác nhận đóng dấu của thủ trưởng cơ quan, đơn vị quản lý trực tiếp và upload file đã xin xác nhận trước khi nộp.
            </Typography>
          </>
        ),
      },
    ],
  };

  // Quá trình học tập, công tác (giữ trong phần thông tin cá nhân)
  const activeProcess = {
    key: "personalProcess",
    values: personalProcess,
    setValues: setPersonalProcess,
    label: "Quá trình học tập, công tác:",
    fields: [
      {
        key: "startAt",
        width: isMobileMode ? 12 : 2,
        label: "Bắt đầu",
        type: "date",
        required: true,
      },
      {
        key: "finishAt",
        width: isMobileMode ? 12 : 2,
        label: "Kết thúc",
        type: "date",
        required: true,
      },
      {
        key: "department",
        width: isMobileMode ? 12 : 3,
        label: "Đơn vị học tập, công tác",
        required: true,
      },
      {
        key: "works",
        width: isMobileMode ? 12 : 4,
        label: "Công việc đảm nhiệm",
        required: true,
      },
    ],
  };

  // B. THÀNH TÍCH KHEN THƯỞNG
  const activeReward = {
    key: "otherAchievement",
    values: otherAchievement,
    setValues: setOtherAchievement,
    label: "Thành tích khen thưởng (Bằng khen, giấy khen...):",
    fields: [
      {
        key: "achievementContent",
        width: isMobileMode ? 12 : 4,
        label: "Nội dung khen thưởng",
        required: true,
      },
      {
        key: "typeOfAchievement",
        width: isMobileMode ? 12 : 3,
        label: "Cấp khen thưởng",
        options: QCV2026_REWARD_LEVEL_OPTIONS,
        required: true,
      },
      {
        key: "achievementFile",
        label: "Minh chứng:",
        width: isMobileMode ? 12 : 4,
        type: "file",
        required: true,
      },
    ],
  };

  // C.1 Hoạt động bồi dưỡng nhân lực trong lĩnh vực xét giải
  const activeHrTraining = {
    key: "hrTraining",
    values: hrTraining,
    setValues: setHrTraining,
    label:
      "Hoạt động bồi dưỡng nhân lực (chia sẻ tri thức, chuyển giao KHCN, tập huấn...) trong lĩnh vực xét giải:",
    fields: [
      {
        key: "content",
        width: isMobileMode ? 12 : 7,
        label: "Hình thức và nội dung thành tích",
        required: true,
      },
      {
        key: "hrTrainingFile",
        label: "Minh chứng",
        width: isMobileMode ? 12 : 4,
        type: "file",
      },
    ],
  };

  // C.2 Hoạt động tình nguyện vì cộng đồng
  const activeSocial = {
    key: "socialActivity",
    values: socialActivity,
    setValues: setSocialActivity,
    label: "Hoạt động tình nguyện vì cộng đồng nói chung:",
    fields: [
      {
        key: "communityAchievement",
        width: isMobileMode ? 12 : 7,
        label: "Hình thức và nội dung thành tích",
      },
      {
        key: "communityAchievementFile",
        label: "Minh chứng",
        width: isMobileMode ? 12 : 4,
        type: "file",
      },
    ],
  };

  const renderActive = (active) => (
    <InputActive
      id={active.key}
      {...active}
      values={active.values}
      setValues={active.setValues}
      setPreview={setPreview}
      handleFileChange={handleFileChange}
      handleChangeForm={handleChangeActivityForm}
    />
  );

  // A. THÀNH TÍCH KHOA HỌC CÔNG NGHỆ — kê khai trực tiếp (mục 1-7 bảng điểm).
  // Mục 8 (Công trình tiêu biểu) là phần "summary" bên dưới.
  const achievementEditors = [
    {
      title: "Bằng độc quyền sáng chế, giải pháp hữu ích thuộc lĩnh vực xét thưởng",
      el: <BangSangChe hideAlert />,
    },
    {
      title: "Bài báo khoa học đăng trên tạp chí quốc tế, quốc gia thuộc lĩnh vực xét thưởng",
      note: "Lưu ý: Ứng viên chỉ khai các bài báo đăng trên các tạp chí thuộc danh mục của Hội đồng chức danh GSNN và Quỹ Nafosted.",
      el: <BaiBao form={KEYS.INTERNATIONAL} hideAlert />,
    },
    // QCV 2026: đã GỘP "Bài báo trong nước" vào mục bài báo phía trên (phân loại
    // "Bài báo, tạp chí trong nước"). Giữ lại - không xóa.
    // {
    //   title: "Bài báo khoa học đăng trên tạp chí trong nước thuộc lĩnh vực xét thưởng",
    //   el: <BaiBao form={KEYS.DOMESTIC} hideAlert />,
    // },
    {
      title: "Bài báo khoa học đăng trên hội thảo quốc tế thuộc lĩnh vực xét thưởng",
      note: "Lưu ý: Danh sách các hội thảo/hội nghị khoa học uy tín áp dụng theo quy định hiện hành của Hội đồng Giáo sư Nhà nước và Quỹ Nafosted.",
      el: <BaoCao hideAlert />,
    },
    {
      title: "Chương trình, dự án, đề tài nghiên cứu khoa học thuộc lĩnh vực xét thưởng",
      el: <DuAn hideAlert />,
    },
    {
      title: "Sách chuyên khảo, giáo trình, chương sách thuộc lĩnh vực xét thưởng",
      note: "Lưu ý: Danh sách các nhà xuất bản uy tín trong và ngoài nước áp dụng theo quy định hiện hành của Hội đồng Giáo sư Nhà nước và Quỹ Nafosted.",
      el: <SachChuyenKhao hideAlert />,
    },
    {
      title: "Sản phẩm khoa học công nghệ được ứng dụng thuộc lĩnh vực xét thưởng",
      note: (
        <>
          Giải thích phạm vi ứng dụng:
          <br />• Phạm vi quốc tế: được ứng dụng ít nhất ở hai quốc gia trở lên
          (VD: Việt Nam, Hàn Quốc…)
          <br />• Phạm vi quốc gia: ứng dụng mới dừng lại ở một quốc gia (VD:
          Việt Nam hoặc Hàn Quốc,…)
          <br />• Phạm vi địa phương/đơn vị: được ứng dụng tại một tỉnh, thành
          hoặc bộ, ngành hoặc cơ quan/đơn vị (VD: TP Hải Phòng hoặc Bộ Nông
          nghiệp và Môi trường hoặc Trường Đại học/Viện nghiên cứu cụ thể)
        </>
      ),
      el: <DoanhNghiep hideAlert />,
    },
    {
      title: "Giải thưởng, huy chương đã đạt được thuộc lĩnh vực xét thưởng",
      el: <GiaiThuong hideAlert />,
    },
  ];

  const topPart = isMobileMode ? textForm.base.slice(0, 4) : textForm.base;
  const botPart = isMobileMode
    ? textForm.base.slice(4, textForm.base.length)
    : [];
  // const partII = textForm.partII;

  return (
    <>
      {/* Form sửa hộ trông y hệt form của chính mình -> phải có dải cảnh báo
          cố định, nếu không quản trị viên rất dễ tưởng đang sửa hồ sơ mình. */}
      {isAdminEditing && (
        <Box
          style={{
            position: "sticky",
            top: 64,
            zIndex: 3,
            marginTop: 64,
            padding: "10px 16px",
            backgroundColor: SECONDARY_COLOR,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <Typography variant="body1">
            Bạn đang sửa hồ sơ của <b>{profile?.name || "ứng viên"}</b> với tư
            cách quản trị viên. Trạng thái hồ sơ giữ nguyên khi lưu.
          </Typography>
          <Box display="flex" style={{ gap: 8 }}>
            <Button
              size="small"
              variant="contained"
              href={`${ROUTER.THONG_TIN_CA_NHAN}/${profile?.id}`}
              style={{ backgroundColor: "#fff" }}
            >
              Sửa thông tin cá nhân
            </Button>
            <Button
              size="small"
              variant="contained"
              href={ROUTER.DANH_SACH_HO_SO}
              style={{ backgroundColor: "#fff" }}
            >
              Thoát
            </Button>
          </Box>
        </Box>
      )}
      <Card style={{ width: "100%", marginTop: isAdminEditing ? 16 : 64 }}>
        <Box height={6} bgcolor="#233871" />
        <Box padding={2}>
          <Typography
            variant="h4"
            style={{
              fontFamily: "SFProDisplay-bold",
              textAlign: "center",
              textTransform: "uppercase",
            }}
          >
            <b>Đăng ký hồ sơ</b>
          </Typography>
        </Box>
      </Card>

      <Card
        style={{
          width: "100%",
          marginTop: 32,
          display: showCSKH ? "block" : "none",
        }}
      >
        <CardHeader
          style={{ paddingRight: 4, paddingTop: 2, paddingBottom: 2 }}
          title={
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6">
                <b>Hỗ trợ đăng ký Giải thưởng KHCN Quả Cầu Vàng</b>
              </Typography>
              <IconButton onClick={() => setShowCSKH(false)}>
                <Close />
              </IconButton>
            </Box>
          }
        />
        <Divider />
        <CardContent style={{ paddingTop: 2, paddingBottom: 2 }}>
          <ul>
            {QCV2026_CONTACTS.map((c, i) => (
              <li key={i} style={{ marginTop: i > 0 ? 4 : 0 }}>
                {`${c.role}: ${c.name} (`}
                <a href={c.zalo || `tel:${c.tel}`}>{c.phone}</a>
                {")"}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <form
        className="dkhs-container"
        id="form-update"
        defaultValue={values}
        onSubmit={handleSubmit}
      >
        <Card style={{ width: "100%", marginTop: 64 }}>
          <Box height={6} bgcolor="#233871" />
          <Box padding={2}>
            <Typography
              variant="h4"
              style={{
                fontFamily: "SFProDisplay-bold",
                textAlign: "center",
                textTransform: "uppercase",
              }}
            >
              <b>Thông tin cá nhân</b>
            </Typography>
          </Box>
        </Card>
        <Card style={{ width: "100%", marginTop: 32 }}>
          <CardContent>
            <Box display="flex">
              <Box flexGrow={1} marginTop="4px">
                <KPTextform
                  component={Box}
                  textForm={topPart}
                  values={values}
                  setValues={setValues}
                  handleSubmit={handleSubmit}
                />
              </Box>
              <Box marginTop="4px" marginLeft={1}>
                <Typography className="kpt-textfield-label">
                  {isMobileMode ? "Ảnh chân dung" : "Ảnh chân dung (4x6)"}
                  <span style={{ color: theme.palette.secondary.main }}>*</span>
                  {":"}
                </Typography>
                <Card className="dkhs-photoContainer">
                  <img
                    alt="anh-chan-dung"
                    className="dkhs-photo"
                    src={photoRender(profile?.photoUrl)}
                  />
                </Card>
              </Box>
            </Box>
            <Box flexGrow={1} marginTop="4px">
              <KPTextform component="div" textForm={botPart} />
            </Box>
          </CardContent>
        </Card>
        <Card style={{ width: "100%", marginTop: 32 }}>
          <CardContent>
            <KPTextform
              component={Box}
              textForm={textForm.partI}
              values={values}
              setValues={setValues}
              handleSubmit={handleSubmit}
            />
          </CardContent>
        </Card>
        
        <Card style={{ width: "100%", marginTop: 32 }}>
          <CardContent>
            <KPTextform
              component={Box}
              textForm={textForm.partII}
              values={values}
              setValues={setValues}
            />
          </CardContent>
        </Card>
        <Card style={{ width: "100%", marginTop: 32 }}>
          <CardContent>{renderActive(activeProcess)}</CardContent>
        </Card>
        <Card style={{ width: "100%", marginTop: 64 }}>
          <Box height={6} bgcolor="#233871" />
          <Box padding={2}>
            <Typography
              variant="h4"
              style={{
                fontFamily: "SFProDisplay-bold",
                textAlign: "center",
                textTransform: "uppercase",
              }}
            >
              <b>Thành tích cá nhân</b>
            </Typography>
            <Box style={{ marginTop: 8 }}>
              <Typography
                variant="body2"
                style={{ textAlign: "justify", fontWeight: 700, color: SECONDARY_COLOR }}
              >
                Lưu ý khi khai hồ sơ:
              </Typography>
              <Typography
                variant="body2"
                style={{ textAlign: "justify", fontStyle: "italic", marginTop: 4, color: SECONDARY_COLOR }}
              >
                - Các minh chứng scan bản dấu đỏ. Trong trường hợp minh chứng
                không phải là tiếng Anh hoặc tiếng Việt thì cần có văn bản dịch
                thuật minh chứng thành tích đó sang tiếng Anh hoặc tiếng Việt có
                xác nhận của cơ quan, đơn vị có thẩm quyền.
              </Typography>
              <Typography
                variant="body2"
                style={{ textAlign: "justify", fontStyle: "italic", marginTop: 4, color: SECONDARY_COLOR }}
              >
                - Các công bố khoa học thống nhất tham chiếu Q theo&nbsp;
                <a
                  href="https://www.scimagojr.com/"
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: SECONDARY_COLOR, textDecoration: "underline" }}
                >
                  https://www.scimagojr.com/
                </a>
                &nbsp;và theo năm đăng ký xét giải (VD: công bố khoa học từ năm
                2020, nhưng đăng ký giải thưởng năm 2026 thì tham chiếu Q theo năm
                2026).
              </Typography>
              <Typography
                variant="body2"
                style={{ textAlign: "justify", fontStyle: "italic", marginTop: 4, color: SECONDARY_COLOR }}
              >
                - Chỉ kê khai các thành tích của ứng viên khi đã thực hiện xong
                (ví dụ: Sáng chế đã được công nhận, bài báo đã đăng, chủ trì đề
                tài đã nghiệm thu… không đánh giá các thành tích đang trong quá
                trình thực hiện).
              </Typography>
            </Box>
          </Box>
        </Card>
        <Card style={{ width: "100%", marginTop: 32 }}>
          <Box padding={2} bgcolor="#f5f1ee">
            <Typography variant="h5">
              <b>A. THÀNH TÍCH KHOA HỌC CÔNG NGHỆ</b>
            </Typography>
          </Box>
        </Card>
        {achievementEditors.map((e, id) => (
          <Card key={id} style={{ width: "100%", marginTop: 32 }}>
            <Box padding={2}>
              <Typography variant="h6">
                <b>{`${id + 1}. ${e?.title}`}</b>
              </Typography>
              {e.note && (
                <Typography
                  variant="body2"
                  style={{
                    fontStyle: "italic",
                    marginTop: 4,
                    color: SECONDARY_COLOR,
                  }}
                >
                  {e.note}
                </Typography>
              )}
            </Box>
            <Divider />
            <Box padding={2}>
              <Grid container spacing={2} justifyContent="flex-end">
                {e.el}
              </Grid>
            </Box>
          </Card>
        ))}
        <Card style={{ width: "100%", marginTop: 32 }}>
          <Box padding={2}>
            <Typography variant="h6">
              <b>8. Công trình tiêu biểu thuộc lĩnh vực xét thưởng</b>
            </Typography>
          </Box>
          <Divider />
          <CardContent>
            <KPTextform
              component={Box}
              textForm={textForm.summary}
              values={values}
              setValues={setValues}
            />
          </CardContent>
        </Card>

        <Card style={{ width: "100%", marginTop: 32 }}>
          <Box padding={2}>
            <Typography variant="h6">
              <b>9. Kế hoạch hành động và cam kết của ứng viên</b>
            </Typography>
          </Box>
          <Divider />
          <CardContent>
            <KPTextform
              component={Box}
              textForm={textForm.actionPlan}
              values={values}
              setValues={setValues}
            />
          </CardContent>
        </Card>

        {/* B. THÀNH TÍCH KHEN THƯỞNG */}
        <Card style={{ width: "100%", marginTop: 32 }}>
          <Box padding={2} bgcolor="#f5f1ee">
            <Typography variant="h5">
              <b>B. THÀNH TÍCH KHEN THƯỞNG</b>
            </Typography>
            <Typography variant="body2" style={{ fontStyle: "italic" }}>
              Các hình thức khen thưởng cấp Nhà nước gồm: Huân chương; Huy
              chương; danh hiệu vinh dự Nhà nước; Giải thưởng Hồ Chí Minh, Giải
              thưởng Nhà nước; Cờ thi đua của Chính phủ; Bằng khen của Thủ tướng
              Chính phủ; Chiến sĩ thi đua toàn quốc.
            </Typography>
          </Box>
          <Divider />
          <CardContent>{renderActive(activeReward)}</CardContent>
        </Card>

        {/* C. THÀNH TÍCH HOẠT ĐỘNG CỘNG ĐỒNG */}
        <Card style={{ width: "100%", marginTop: 32 }}>
          <Box padding={2} bgcolor="#f5f1ee">
            <Typography variant="h5">
              <b>C. THÀNH TÍCH HOẠT ĐỘNG CỘNG ĐỒNG</b>
            </Typography>
          </Box>
          <Divider />
          <CardContent>
            {renderActive(activeHrTraining)}
            <Divider
              style={{ width: "calc(100% + 32px)", margin: "16px -16px" }}
            />
            {renderActive(activeSocial)}
          </CardContent>
        </Card>

        {/* D. ƯU TIÊN */}
        <Card style={{ width: "100%", marginTop: 32 }}>
          <Box padding={2} bgcolor="#f5f1ee">
            <Typography variant="h5">
              <b>D. ƯU TIÊN</b>
            </Typography>
          </Box>
          <Divider />
          <CardContent>
            <Grid container spacing={1}>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={!!priority?.isEthnicMinority}
                      onChange={(e) =>
                        setPriority({
                          ...priority,
                          isEthnicMinority: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Là người dân tộc thiểu số"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={!!priority?.isDisabled}
                      onChange={(e) =>
                        setPriority({
                          ...priority,
                          isDisabled: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Là người khuyết tật"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card style={{ width: "100%", marginTop: 32 }}>
          <CardContent>
            <KPTextform
              component={Box}
              textForm={textForm.confirmation}
              values={values}
              setValues={setValues}
            />
          </CardContent>
        </Card>
        <Card style={{ width: "100%", marginTop: 16 }}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={acceptRules}
                      onChange={() => setAcceptRules(!acceptRules)}
                    />
                  }
                  label={
                    <Typography variant="subtitle1">
                      <i style={{ fontFamily: "SFProDisplay" }}>
                        Tôi xin hoàn toàn chịu trách nhiệm trước pháp luật, Ban
                        tổ chức Giải thưởng về tính chính xác và các thông tin
                        đã khai trong hồ sơ đăng ký trực tuyến tham gia xét giải
                        thưởng
                      </i>
                    </Typography>
                  }
                />
              </Grid>
              {/* Quản trị viên sửa hộ: 1 nút duy nhất, trạng thái hồ sơ giữ
                  nguyên (xem handleSave) nên không có khái niệm nháp/nộp. */}
              {isAdminEditing ? (
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    // Không bắt quản trị viên tick ô cam kết của ứng viên.
                    disabled={loading}
                    color="secondary"
                    variant="contained"
                    onClick={() => handleSave(application?.status)}
                  >
                    Lưu thay đổi
                  </Button>
                </Grid>
              ) : (
                <>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      // loading=true trong lúc đang nạp danh sách thành tích theo
                      // season (reloadSeasonAchievements) - lưu lúc này sẽ gửi
                      // achievementData rỗng/thiếu lên backend, mà backend luôn
                      // xoá-rồi-tạo-lại toàn bộ bảng nối theo đúng payload -> xoá
                      // mất thành tích thật đã lưu trước đó. Khoá nút Lưu tới khi
                      // nạp xong để tránh lặp lại lỗi mất dữ liệu.
                      disabled={!acceptRules || loading}
                      variant="outlined"
                      color="primary"
                      onClick={() => handleSave("unsubmit")}
                    >
                      Lưu bản nháp
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      disabled={!acceptRules || loading}
                      type="submit"
                      color="secondary"
                      variant="contained"
                    >
                      Nộp hồ sơ
                    </Button>
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
        </Card>
      </form>

      <Dialog
        fullWidth
        maxWidth="xs"
        open={!loading && (isNotAvalaible || warningOpen)}
      >
        <DialogTitle style={{ padding: "4px 12px 4px 24px" }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h5">
              <b>Thông báo</b>
            </Typography>
            {!isNotAvalaible && (
              <IconButton onClick={() => setWarningOpen(false)}>
                <Close />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" width="100%" marginBottom={4}>
            <WarningRounded
              style={{ margin: "auto", color: "#4da6ff", fontSize: 72 }}
            />
          </Box>
          <Typography variant="h6" style={{ textAlign: "center" }}>
            {isNotAvalaible ? (
              <>
                {isNotAvalaible}
                <br />
                <b>
                  Giải thưởng Khoa học công nghệ <br /> Quả Cầu Vàng năm{" "}
                  {openSeason?.year}
                </b>
              </>
            ) : (
              <b>Hồ sơ đã được nộp trên hệ thống!</b>
            )}
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: "8px 24px" }}>
          {isNotAvalaible ? (
            <Button
              fullWidth
              color="secondary"
              variant="contained"
              href={ROUTER.HOME}
            >
              Về trang chủ
            </Button>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  href={ROUTER.QCV.HOME}
                  variant="outlined"
                  startIcon={<Home />}
                  onClick={() => setWarningOpen(false)}
                >
                  Trang chủ
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  color="secondary"
                  variant="contained"
                  startIcon={<Edit />}
                  style={{ float: "right" }}
                  onClick={() => setWarningOpen(false)}
                >
                  Chỉnh sửa hồ sơ
                </Button>
              </Grid>
            </Grid>
          )}
        </DialogActions>
      </Dialog>

      <PreviewModal
        open={!!preview}
        url={preview}
        handleClose={() => setPreview(null)}
      />
    </>
  );
}

export default DangKyHoSoUpdate;
